import React, { useState, useRef } from 'react';
import { db, storage } from '../firebase';
import { ref, deleteObject } from 'firebase/storage';
import { doc, updateDoc, serverTimestamp, arrayUnion, arrayRemove } from 'firebase/firestore';
import { claimPatch } from '../data/claimWrite';
import { CODES, LABELS_CONFIRMED, groupLineages } from '../data/documentVersion';
// SECTION 412 PART A: uploads no longer write Storage directly (that path is off in storage.rules).
// They go through the claimDocumentUploadUrl callable, which verifies claim membership - one helper,
// shared with QuickFile and NewClaim, so the marking and the mediated path cannot drift (SS XI).
import { uploadClaimDocument } from '../data/claimDocumentClient';
import { mayAttachDocuments } from '../data/claimLadder';   // forensic audit 2026-10-16: an officer uploads where he may record the document
import { viewStoredFile } from '../data/scannedFile';   // Record 591 (ruling 247): View fetches the link when clicked, after the scan

const DOCUMENT_TYPES = [
  'Claim Letter',
  'Letter of Non-Acquiescence',
  'GC Position Statement',
  'GC Directive',
  'GC Advisory',
  'Agreement / Contract',
  'Arbitration Award',
  'Exhibit',
  'Timesheet / Pay Record',
  'Witness Statement',
  'Correspondence',
  'Medical Record',
  'Photo / Image',
  'Other',
];

function formatFileSize(bytes) {
  if (bytes < 1024) return bytes + ' B';
  if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
  return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
}

function DocIcon({ type }) {
  const icons = {
    'application/pdf': '📄',
    'image/': '🖼️',
    'application/msword': '📝',
    'application/vnd.openxmlformats': '📝',
    'text/': '📃',
  };
  for (const [key, icon] of Object.entries(icons)) {
    if (type?.startsWith(key)) return <span>{icon}</span>;
  }
  return <span>📎</span>;
}

// THE BADGE. While LABELS_CONFIRMED is false this prints the CODE ALONE - a bare "DV" is honest,
// a guessed expansion on a page a Board reads is not. Bob has the letters; the words are his to give.
function VersionBadge({ code, tone }) {
  const c = tone === 'live' ? '#27AE60' : tone === 'final' ? '#4A9EE8' : tone === 'unmarked' ? '#E8A020' : '#8AA4BF';
  return (
    <span style={{ fontSize: 10, fontWeight: 700, letterSpacing: 1, padding: '2px 6px',
                   border: '1px solid ' + c, color: c, borderRadius: 3, whiteSpace: 'nowrap' }}>
      {code}
    </span>
  );
}

export default function ClaimDocuments({ claimId, claim, documents = [], user, onDocumentsUpdated }) {
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [docType, setDocType] = useState('Exhibit');
  const [docDescription, setDocDescription] = useState('');
  const [error, setError] = useState('');
  const [deleting, setDeleting] = useState(null);
  // RULING 257 (section 517): the uploader confirms a copy is the SIGNED claim; recorded with his name and the time.
  const [signedCopy, setSignedCopy] = useState(false);
  // Record 613 (Bob 10/8), ruling 157: a recording is stored only when the carrier produced it - the uploader says so here.
  const [carrierRecording, setCarrierRecording] = useState(false);

  // VERSION MARKING (ruling of 2026-08-29). `versionOf` is the lineage this upload joins - empty
  // means it starts a new one. `versionRole` is live (DV) or final (FV); anything else is
  // positional and the module decides it.
  const [versionOf, setVersionOf] = useState('');
  const [versionRole, setVersionRole] = useState('live');
  const [versionReason, setVersionReason] = useState('');
  const fileRef = useRef();

  // The grouping is a pure function in src\data\documentVersion.js and is proved by
  // scripts\test-document-version.mjs. This page renders what it returns and decides nothing.
  const lineages = groupLineages(documents);
  const markedLineages = lineages.filter(l => !l.unmarked);
  const unmarkedCount = lineages.filter(l => l.unmarked).length;

  // Section 517: the claimant who filed her own claim may attach to it once filed (the callable and the rules'
  // ownerAttachesDocuments() already allowed it; this page did not offer it) - she saves her signed copy here.
  // Forensic audit 2026-10-16: an officer was offered upload by ROLE alone, so a Local Chairperson on a claim past his
  // rung (gc_review and above) uploaded the file to Storage and then had the claim record refused - an orphan object.
  // An officer is now offered upload exactly where the rules let him record a document: claimLadder.canEdit(), the
  // client mirror of lcEdit/gcEdit/adminEdit (an LC: submitted..documentation_review, in his scope). The member's arm
  // (the creator, once filed) is unchanged. Both live in claimLadder.mayAttachDocuments().
  const canUpload = mayAttachDocuments(claim, user);
  const canDelete = !!user?.isAdmin;   // Record 592 (F24): storage.rules allows delete for admin only - a GC's Delete always failed

  const handleUpload = async (e) => {
    const files = Array.from(e.target.files);
    if (!files.length) return;
    setUploading(true);
    setError('');
    setUploadProgress(0);

    try {
      const uploaded = [];
      // Forensic audit 2026-10-16: every file in a multi-file pick computed its mark from the SAME `documents` list, so
      // three files sent as versions of one document all came out V1 (or two DVs). Each entry joins this working copy
      // before the next file is marked, so numbering is monotonic and the one-DV rule sees the copy just made.
      const working = Array.isArray(documents) ? documents.slice() : [];
      let stoppedAt = null;
      for (let i = 0; i < files.length; i++) {
        const file = files[i];
        let entry;
        try {
          // THE MEDIATED UPLOAD (section 412 Part A). The callable verifies claim membership with the
          // Admin SDK and bakes the metadata the read rule trusts; the mark is assigned by
          // documentVersion.js inside the helper (one-DV rule, monotonic numbering), and a refused mark
          // or a refused membership check refuses THIS file rather than uploading an unmarked or
          // mis-scoped one. Version control (Version of / This copy is) is officer input, carried through.
          entry = await uploadClaimDocument({
            claimId, file, docType, description: docDescription,
            documents: working, versionOf, versionRole, versionReason, user,
            mediaOrigin: carrierRecording ? 'carrier_produced' : undefined,   // Record 613 (Bob 10/8), ruling 157
            onProgress: (pct) => setUploadProgress(Math.round(((i / files.length) + (pct / 100 / files.length)) * 100)),
          });
        } catch (fileErr) {
          // A refused file (a second DV, a bad type) stops the run - but the files already uploaded are still recorded
          // below, so none of them is left in Storage with no record on the claim.
          stoppedAt = { name: file.name, why: (fileErr && fileErr.message) || 'refused' };
          break;
        }
        working.push(entry);
        uploaded.push(signedCopy ? { ...entry, signedCopy: true, signedConfirmedBy: user?.name || user?.email || 'Unknown', signedConfirmedAt: new Date().toISOString() } : entry);
      }

      if (uploaded.length) {
        await updateDoc(doc(db, 'claims', claimId), claimPatch({
          documents: arrayUnion(...uploaded),
          updatedAt: serverTimestamp(),
        }, user));
        if (onDocumentsUpdated) onDocumentsUpdated(uploaded);
      }
      if (stoppedAt) {
        throw new Error('"' + stoppedAt.name + '" was NOT attached: ' + stoppedAt.why
          + (uploaded.length ? ' The ' + uploaded.length + ' file(s) before it were attached.' : '')
          + (files.length - uploaded.length > 1 ? ' The files after it were not sent.' : ''));
      }

      setDocDescription('');
      setSignedCopy(false);
      setCarrierRecording(false);   // Record 613
      setVersionReason('');
      setVersionOf('');
      setUploadProgress(0);
      if (fileRef.current) fileRef.current.value = '';
    } catch (err) {
      setError('Upload failed: ' + err.message);
    } finally {
      setUploading(false);
    }
  };

  const handleDelete = async (document) => {
    if (!window.confirm(`Delete "${document.name}"? This cannot be undone.`)) return;
    setDeleting(document.storagePath);
    // Forensic audit 2026-10-16: the record goes first, then the file. The other order deleted the Storage object and,
    // if the record write was then refused, left the claim listing a document whose file no longer existed. A refused
    // record now leaves both intact; a failed file delete after it leaves only an unlisted object, and says so.
    let recordRemoved = false;
    try {
      await updateDoc(doc(db, 'claims', claimId), claimPatch({
        documents: arrayRemove(document),
        updatedAt: serverTimestamp(),
      }, user));
      recordRemoved = true;
      if (onDocumentsUpdated) onDocumentsUpdated(null, document);
      const storageRef = ref(storage, document.storagePath);
      await deleteObject(storageRef);
    } catch (err) {
      setError(recordRemoved
        ? 'The document was removed from the claim, but its stored file could not be deleted: ' + err.message
        : 'Delete failed - nothing was removed: ' + err.message);
    } finally {
      setDeleting(null);
    }
  };

  return (
    <div className="card" style={{ marginBottom: 16 }}>
      <div className="card-header">
        <div className="card-title">Documents &amp; Exhibits</div>
        <div style={{ fontSize: 11, color: '#8AA4BF' }}>
          {documents.length} file{documents.length !== 1 ? 's' : ''} attached
          {unmarkedCount > 0 && (
            <span style={{ color: '#E8A020' }}>
              {'  -  ' + unmarkedCount + ' unmarked'}
            </span>
          )}
        </div>
      </div>

      {error && (
        <div style={{ background: '#2D0A0A', border: '1px solid #C0392B', borderRadius: 6, padding: '10px 14px', marginBottom: 12, color: '#FF6B6B', fontSize: 12 }}>
          ⚠ {error}
        </div>
      )}

      {/* Document List */}
      {documents.length > 0 ? (
        <div style={{ marginBottom: 16 }}>
          {documents.map((d, i) => (
            <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 12, background: '#0A1628', border: '1px solid #2E4A6B', borderRadius: 6, padding: '10px 14px', marginBottom: 8 }}>
              <div style={{ fontSize: 20 }}><DocIcon type={d.fileType} /></div>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, minWidth: 0 }}>
                  {/* An UNMARKED document says so. It is not called the original, because nobody
                      decided it was - it simply predates marking. */}
                  <VersionBadge
                    code={d.versionCode || 'UNMARKED'}
                    tone={!d.versionCode ? 'unmarked'
                        : d.versionCode === CODES.DV ? 'live'
                        : d.versionCode === CODES.FV ? 'final' : 'old'} />
                  <div style={{ fontSize: 13, color: d.versionCode && d.versionCode !== CODES.DV && d.versionCode !== CODES.FV ? '#8AA4BF' : '#F4F6F8',
                                fontWeight: 600, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{d.name}</div>
                </div>
                <div style={{ fontSize: 11, color: '#8AA4BF', marginTop: 2 }}>
                  <span style={{ color: '#E8A020', marginRight: 8 }}>{d.type}</span>
                  {d.signedCopy && <span style={{ color: '#5FB878', fontWeight: 700, marginRight: 8 }}>SIGNED COPY</span>}
                  {d.description && <span style={{ marginRight: 8 }}>{d.description}</span>}
                  {formatFileSize(d.fileSize)} · {d.uploadedBy} · {new Date(d.uploadedAt).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                </div>
                {d.versionWatermark && (
                  <div style={{ fontSize: 10, color: 'var(--steel)', marginTop: 3, fontFamily: 'monospace' }}>
                    {d.versionWatermark}
                  </div>
                )}
              </div>
              <div style={{ display: 'flex', gap: 8, flexShrink: 0 }}>
                <a href={d.downloadURL || '#'} onClick={viewStoredFile(storage, d.downloadURL, d.storagePath)} target="_blank" rel="noopener noreferrer"
                  style={{ fontSize: 11, color: '#4A9EE8', textDecoration: 'none', padding: '4px 10px', border: '1px solid #2E4A6B', borderRadius: 4 }}>
                  View
                </a>
                {canDelete && (
                  <button
                    onClick={() => handleDelete(d)}
                    disabled={deleting === d.storagePath}
                    style={{ fontSize: 11, color: '#C0392B', background: 'transparent', border: '1px solid #C0392B', borderRadius: 4, padding: '4px 10px', cursor: 'pointer' }}>
                    {deleting === d.storagePath ? '...' : 'Delete'}
                  </button>
                )}
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div style={{ color: 'var(--steel)', fontSize: 13, marginBottom: 14 }}>No documents attached yet.</div>
      )}

      {/* Upload Section */}
      {canUpload && (
        <div style={{ background: '#0D1B2A', border: '1px solid #2E4A6B', borderRadius: 8, padding: '14px 16px' }}>
          <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: 2, textTransform: 'uppercase', color: '#8AA4BF', marginBottom: 12 }}>
            Attach Document
          </div>
          {/* THE VERSION CONTROL. A new document starts its own lineage; a revision joins one. */}
          <div style={{ display: 'flex', gap: 10, marginBottom: 10 }}>
            <div style={{ flex: 1 }}>
              <label style={{ fontSize: 10, color: '#8AA4BF', textTransform: 'uppercase', letterSpacing: 1, display: 'block', marginBottom: 4 }}>Version of</label>
              <select aria-label="Version of" className="form-select" value={versionOf} onChange={e => setVersionOf(e.target.value)} style={{ fontSize: 12 }}>
                <option value="">A new document (starts its own lineage)</option>
                {markedLineages.map(l => (
                  <option key={l.key} value={l.key}>
                    {(l.live || l.entries[0]).name} &mdash; {l.entries.length} version(s)
                  </option>
                ))}
              </select>
            </div>
            <div style={{ flex: 1 }}>
              <label style={{ fontSize: 10, color: '#8AA4BF', textTransform: 'uppercase', letterSpacing: 1, display: 'block', marginBottom: 4 }}>This copy is</label>
              <select aria-label="This copy is" className="form-select" value={versionRole} onChange={e => setVersionRole(e.target.value)} style={{ fontSize: 12 }}>
                <option value="live">The live editable copy (DV)</option>
                <option value="">A superseded version (OV, V1, V2...)</option>
                <option value="final">The final version (FV)</option>
              </select>
            </div>
            <div style={{ flex: 1 }}>
              <label style={{ fontSize: 10, color: '#8AA4BF', textTransform: 'uppercase', letterSpacing: 1, display: 'block', marginBottom: 4 }}>Why this version (optional)</label>
              <input aria-label="Why this version (optional)" className="form-input" style={{ fontSize: 12 }} placeholder="e.g. corrected the date of occurrence"
                     value={versionReason} onChange={e => setVersionReason(e.target.value)} />
            </div>
          </div>

          {/* ISS-46, STATED ON THE PAGE RATHER THAN LEFT TO BE DISCOVERED. The mark below is
              METADATA and a filename suffix. It is NOT printed onto the file. */}
          <div style={{ border: '1px solid #E8A020', background: '#2D1A00', color: '#E8A020',
                        padding: '8px 12px', marginBottom: 10, fontSize: 11, lineHeight: 1.6 }}>
            <b>The mark goes on the record and on the file name, not yet onto the page.</b> Rendering
            it onto a copy of the document needs a server-side step (ISS-46); until that exists, a
            reader who opens the file sees an unmarked document.
            {!LABELS_CONFIRMED && <> Codes print bare &mdash; <b>DV, OV, V1, FV</b> &mdash; because the
            words behind the letters have not been confirmed, and a guessed expansion on a document a
            Board reads is worse than a letter.</>}
          </div>

          <div style={{ display: 'flex', gap: 10, marginBottom: 10 }}>
            <div style={{ flex: 1 }}>
              <label style={{ fontSize: 10, color: '#8AA4BF', textTransform: 'uppercase', letterSpacing: 1, display: 'block', marginBottom: 4 }}>Document Type</label>
              <select aria-label="Document Type" className="form-select" value={docType} onChange={e => setDocType(e.target.value)} style={{ fontSize: 12 }}>
                {DOCUMENT_TYPES.map(t => <option key={t} value={t}>{t}</option>)}
              </select>
            </div>
            <div style={{ flex: 2 }}>
              <label style={{ fontSize: 10, color: '#8AA4BF', textTransform: 'uppercase', letterSpacing: 1, display: 'block', marginBottom: 4 }}>Description (optional)</label>
              <input aria-label="Description (optional)" className="form-input" style={{ fontSize: 12 }} placeholder="Brief description of this document..." value={docDescription} onChange={e => setDocDescription(e.target.value)} />
            </div>
          </div>
          <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12, color: '#DCE7F2', marginBottom: 10, cursor: 'pointer' }}>
            <input aria-label="This copy is the signed claim (it is recorded with your name and the time)" type="checkbox" checked={signedCopy} onChange={e => setSignedCopy(e.target.checked)} />
            This copy is the signed claim (it is recorded with your name and the time)
          </label>
          {/* Record 613 (Bob 10/8), ruling 157: only a carrier-produced recording is stored; any other recording is refused
              and listed in the Recordings tracker below instead. */}
          <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12, color: '#DCE7F2', marginBottom: 10, cursor: 'pointer' }}>
            <input aria-label="Carrier recording" type="checkbox" checked={carrierRecording} onChange={e => setCarrierRecording(e.target.checked)} />
            This is a recording (audio or video) produced by the carrier. A recording made by the organization or a member is not stored - list it in the Recordings tracker.
          </label>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            <input aria-label="Attach a document to this claim"
              ref={fileRef}
              type="file"
              multiple
              accept=".pdf,.doc,.docx,.jpg,.jpeg,.png,.txt,.xls,.xlsx,.mp3,.m4a,.wav,.mp4,.mov"
              onChange={handleUpload}
              disabled={uploading}
              style={{ display: 'none' }}
              id={`file-upload-${claimId}`}
            />
            <label
              htmlFor={`file-upload-${claimId}`}
              style={{
                padding: '8px 16px', background: uploading ? '#1A2E44' : '#1A3A5C',
                border: '1px solid #2E4A6B', borderRadius: 6, fontSize: 12,
                color: uploading ? 'var(--steel)' : '#F4F6F8', cursor: uploading ? 'not-allowed' : 'pointer',
                display: 'inline-block',
              }}>
              {uploading ? `Uploading... ${uploadProgress}%` : '📎 Select Files'}
            </label>
            <div style={{ fontSize: 11, color: 'var(--steel)' }}>PDF, Word, Excel, images, a carrier-produced recording (tick the box) — multiple files allowed</div>
          </div>
          {uploading && (
            <div style={{ marginTop: 10 }}>
              <div style={{ background: '#1A2E44', borderRadius: 4, height: 6, overflow: 'hidden' }}>
                <div style={{ background: '#E8A020', height: '100%', width: `${uploadProgress}%`, transition: 'width 0.3s' }} />
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
